import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Loader2 } from 'lucide-react';
import Button from './Button';

interface ServiceFormPopupProps {
  isOpen: boolean;
  onClose: () => void;
  preSelectedService?: string;
  initialScrollPosition?: number;
}

interface FormData {
  service: string;
  name: string;
  contact: string;
  message: string;
  requestCallback: boolean;
}

interface FormErrors {
  name?: string;
  contact?: string;
}

const ServiceFormPopup: React.FC<ServiceFormPopupProps> = ({ isOpen, onClose, preSelectedService, initialScrollPosition = 0 }) => {
  const [formData, setFormData] = useState<FormData>({
    service: '',
    name: '',
    contact: '',
    message: '',
    requestCallback: false
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Service options mapping
  const serviceOptions = [
    'CCTV Streaming',
    'Websites that Grow with You',
    'Starter Brand Kit & More',
    'CCTV + Website Starter Bundle'
  ];

  // Pre-fill service when prop changes
  useEffect(() => {
    if (preSelectedService && serviceOptions.includes(preSelectedService)) {
      setFormData(prev => ({
        ...prev,
        service: preSelectedService
      }));
    }
  }, [preSelectedService]);

  // Reset form when popup closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        service: preSelectedService || '',
        name: '',
        contact: '',
        message: '',
        requestCallback: false
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, preSelectedService]);

  // Use scroll position from context
  
  useEffect(() => {
    if (isOpen && initialScrollPosition >= 0) {
      // Apply body lock with scroll position preserved
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${initialScrollPosition}px`;
      document.body.style.left = '0';
    } else if (!isOpen) {
      // Read the saved scroll from body.top before clearing styles
      const savedScroll = Math.abs(parseInt(document.body.style.top || '0', 10));
      
      // Remove body lock
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.left = '';
      
      // Restore scroll position immediately
      if (savedScroll > 0) {
        window.scrollTo({ top: savedScroll, behavior: 'instant' });
      }
    }

    return () => {
      // Cleanup - read saved scroll, then restore
      const savedScroll = Math.abs(parseInt(document.body.style.top || '0', 10));
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.left = '';
      if (savedScroll > 0) {
        window.scrollTo({ top: savedScroll, behavior: 'instant' });
      }
    };
  }, [isOpen, initialScrollPosition]);

  // Handle touch events for mobile swipe-to-close
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isDownSwipe = distance < -150; // Swipe down more than 150px
    if (isDownSwipe) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.contact.trim()) {
      newErrors.contact = 'Email or phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatWhatsAppMessage = (): string => {
    const name = formData.name.trim();
    const service = formData.service;
    const contact = formData.contact.trim();
    const message = formData.message.trim();
    const requestCallback = formData.requestCallback;

    let whatsappMessage = `Hi, I'm *${name}*.%0AI'm interested in: *${service}*%0A*Contact:* ${contact}`;
    
    if (requestCallback) {
      whatsappMessage += `%0A%0A📞 Please call me back - I would prefer a phone consultation.`;
    }
    
    if (message) {
      whatsappMessage += `%0A*Message:* ${message}`;
    }

    return whatsappMessage;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Build WhatsApp URL
      const whatsappMessage = formatWhatsAppMessage();
      const whatsappUrl = `https://wa.me/919774477416?text=${whatsappMessage}`; // external messaging link
      
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Open WhatsApp in new tab
      window.open(whatsappUrl, '_blank');
      
      // Close the popup after successful redirect
      onClose();
    } catch (error) {
      console.error('Error opening WhatsApp:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div 
        className="service-popup animate-in relative w-full sm:w-full sm:max-w-md bg-background border-0 sm:border-2 border-foreground shadow-2xl rounded-t-2xl sm:rounded-lg max-h-[90vh] sm:max-h-none overflow-y-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Mobile handle bar */}
        <div className="sm:hidden flex justify-center pt-2 pb-1 cursor-pointer" onClick={onClose}>
          <div className="w-10 h-1 bg-foreground/30 rounded-full"></div>
        </div>
        {/* Header */}
        <div className="relative p-4 sm:p-6 pb-2 sm:pb-4">
          <button
            onClick={onClose}
            className="absolute top-2 sm:top-4 right-2 sm:right-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-foreground/10 rounded-full transition-colors z-10"
            aria-label="Close popup"
          >
            <X size={20} className="sm:w-5 sm:h-5" />
          </button>
          
          <h2 className="text-xl sm:text-2xl font-bold text-foreground pr-12">
            Let's build something great together.
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 pt-0 pb-4 sm:pb-6">
          {/* Service Selection */}
          <div className="mb-4 sm:mb-4">
            <label htmlFor="service" className="block text-sm font-semibold text-foreground mb-2">
              Service Selected *
            </label>
            <select
              id="service"
              value={formData.service}
              onChange={(e) => handleInputChange('service', e.target.value)}
              className="w-full p-3 sm:p-3 border-2 border-foreground/20 bg-background text-foreground focus:border-primary focus:border-[3px] focus:outline-none transition-all duration-200 rounded-lg text-base"
              required
            >
              <option value="">Select a service</option>
              {serviceOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* Name */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
              Your Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full p-3 sm:p-3 border-2 border-foreground/20 bg-background text-foreground focus:border-primary focus:border-[3px] focus:outline-none transition-all duration-200 rounded-lg text-base"
              placeholder="Enter your name"
              autoComplete="name"
              required
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Contact */}
          <div className="mb-4">
            <label htmlFor="contact" className="block text-sm font-semibold text-foreground mb-2">
              Your Email / Phone *
            </label>
            <input
              type="text"
              id="contact"
              value={formData.contact}
              onChange={(e) => handleInputChange('contact', e.target.value)}
              className="w-full p-3 sm:p-3 border-2 border-foreground/20 bg-background text-foreground focus:border-primary focus:border-[3px] focus:outline-none transition-all duration-200 rounded-lg text-base"
              placeholder="Enter your email or phone number"
              autoComplete="tel email"
              required
            />
            {errors.contact && (
              <p className="text-red-600 text-sm mt-1">{errors.contact}</p>
            )}
          </div>

          {/* Message */}
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-semibold text-foreground mb-2">
              Message (optional)
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              className="w-full p-3 sm:p-3 border-2 border-foreground/20 bg-background text-foreground focus:border-primary focus:border-[3px] focus:outline-none transition-all duration-200 resize-none rounded-lg text-base"
              placeholder="Brief us on your project. Scope of work, timeline, budget, etc."
              rows={3}
            />
          </div>

          {/* Callback Request */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={formData.requestCallback}
                onChange={(e) => handleInputChange('requestCallback', e.target.checked)}
                className="mt-[3px] w-5 h-5 min-w-[20px] bg-background border-2 border-foreground/20 rounded focus:outline-none transition-all duration-200 cursor-pointer accent-primary"
              />
              <div className="flex-1">
                <span className="text-sm font-semibold text-black group-hover:text-black relative group-hover:after:content-[''] group-hover:after:absolute group-hover:after:bottom-0 group-hover:after:left-0 group-hover:after:w-full group-hover:after:h-0.5 group-hover:after:bg-primary transition-all duration-200">
                  Request a callback
                </span>
                <p className="text-xs text-foreground/70 mt-1">
                  Check this if you'd prefer us to call you back for a consultation
                </p>
              </div>
            </label>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="primary"
            className="w-full py-4 sm:py-4 text-base sm:text-lg font-semibold min-h-[3rem] touch-manipulation"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <Loader2 size={20} className="animate-spin" />
                Sending...
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <MessageCircle size={20} />
                Send
              </div>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ServiceFormPopup;
